import { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import parse, { domToReact } from "html-react-parser";
import { AI_ASSISTANT_BLUE, MESSAGE_STATUS } from "./constants";
import { looksLikeMarkdown, renderMarkdown } from "./markdown";

function looksLikeHtml(text) {
  return /<\/?[a-z][\s\S]*>/i.test(String(text || ""));
}

/** Strip inline styles that break layout or blow up heading size in the bubble. */
function stripAntiWrapStyles(domNode) {
  if (domNode?.type !== "tag" || !domNode.attribs?.style) return;
  const tag = String(domNode.name || "").toLowerCase();
  const isHeading = /^h[1-6]$/.test(tag);
  const next = String(domNode.attribs.style)
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => {
      const prop = part.split(":")[0]?.trim().toLowerCase();
      if (
        [
          "white-space",
          "word-break",
          "overflow-wrap",
          "word-wrap",
          "width",
          "min-width",
          "max-width",
        ].includes(prop)
      ) {
        return false;
      }
      // Drop huge inline font sizes on headings / strong titles.
      if (isHeading && (prop === "font-size" || prop === "line-height")) return false;
      return true;
    })
    .join("; ");
  if (next) domNode.attribs.style = next;
  else delete domNode.attribs.style;
}

const bubbleContentSx = {
  // Grid forces children into a definite column so text wraps instead of
  // expanding past the bubble background (flex min-content overflow).
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr)",
  minWidth: 0,
  maxWidth: "100%",
  fontSize: 14,
  lineHeight: 1.55,
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  "& *": {
    maxWidth: "100% !important",
    minWidth: "0 !important",
    overflowWrap: "anywhere !important",
    wordBreak: "break-word !important",
    boxSizing: "border-box",
  },
  "& p": {
    margin: 0,
    mb: 1,
    fontSize: "14px !important",
    lineHeight: 1.55,
    fontWeight: 400,
    whiteSpace: "normal !important",
  },
  "& p:last-of-type": { mb: 0 },
  // Theme h1/h2 are page-scale — keep chat headings compact.
  "& h1": {
    margin: 0,
    mb: 1,
    fontSize: "16px !important",
    lineHeight: 1.35,
    fontWeight: 700,
    whiteSpace: "normal !important",
  },
  "& h2": {
    margin: 0,
    mb: 0.85,
    fontSize: "15px !important",
    lineHeight: 1.35,
    fontWeight: 700,
    whiteSpace: "normal !important",
  },
  "& h3, & h4, & h5, & h6": {
    margin: 0,
    mb: 0.75,
    fontSize: "14px !important",
    lineHeight: 1.4,
    fontWeight: 700,
    whiteSpace: "normal !important",
  },
  "& ul, & ol": {
    margin: "6px 0 0",
    paddingLeft: 2.5,
    paddingRight: 0,
    fontSize: "14px !important",
    whiteSpace: "normal !important",
  },
  "& li": {
    marginBottom: 0.75,
    fontSize: "14px !important",
    lineHeight: 1.55,
    whiteSpace: "normal !important",
  },
  "& li > p": { margin: "0 0 4px", fontSize: "14px !important" },
  "& li > ul": { margin: "2px 0 6px", paddingLeft: 2 },
  "& li em": { color: "#64748b", fontSize: "13px !important" },
  "& strong, & b": { fontWeight: 700, fontSize: "inherit" },
  "& a": { color: AI_ASSISTANT_BLUE },
  "& pre, & code": {
    whiteSpace: "pre-wrap !important",
    display: "block",
    fontSize: "13px !important",
  },
  "& table": { display: "block", overflowX: "auto" },
  "& img": { height: "auto" },

  // Presentation proposal deck — visual slide + library media title cards
  "& .mic-pres-deck": { display: "grid", gap: "10px", minWidth: 0 },
  "& .mic-pres-intro": {
    margin: "0 !important",
    fontSize: "15px !important",
    fontWeight: 700,
    color: "ink.strong",
  },
  "& .mic-pres-meta": {
    margin: "0 !important",
    fontSize: "12.5px !important",
    color: "#64748b",
  },
  "& .mic-pres-footer": {
    margin: "4px 0 0 !important",
    fontSize: "12.5px !important",
    color: "#64748b",
  },
  "& .mic-pres-slides": { display: "grid", gap: "10px", minWidth: 0 },
  "& .mic-pres-slide": {
    margin: 0,
    padding: "10px 12px",
    borderRadius: "12px",
    border: "1px solid rgba(148,163,184,0.32)",
    bgcolor: "background.elevated",
    minWidth: 0,
  },
  "& .mic-pres-slide-compact": { padding: "8px 10px" },
  "& .mic-pres-slide-media": {
    borderColor: "rgba(0, 97, 254, 0.28)",
    background: "linear-gradient(180deg, #ffffff 0%, #f0f7ff 100%)",
  },
  "& .mic-pres-slide-head": {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    marginBottom: "6px",
  },
  "& .mic-pres-slide-num": {
    flexShrink: "0 !important",
    width: "22px !important",
    minWidth: "22px !important",
    height: "22px",
    borderRadius: "50%",
    display: "inline-grid",
    placeItems: "center",
    fontSize: "11px !important",
    fontWeight: 800,
    color: AI_ASSISTANT_BLUE,
    background: "rgba(0, 97, 254, 0.1)",
  },
  "& .mic-pres-slide-title": {
    margin: "0 !important",
    flex: "1 1 auto",
    minWidth: 0,
    fontSize: "13.5px !important",
    lineHeight: 1.35,
    fontWeight: 700,
    color: "ink.strong",
  },
  "& .mic-pres-slide-actions": {
    marginLeft: "auto",
    display: "inline-flex",
    flexShrink: 0,
    gap: "6px",
  },
  "& .mic-pres-bullets": {
    margin: "0 0 8px !important",
    paddingLeft: "28px !important",
    color: "ink.body",
  },
  "& .mic-pres-bullets li": {
    marginBottom: "3px !important",
    fontSize: "12.5px !important",
  },
  "& .mic-media-card": {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginTop: "4px",
    padding: "8px 10px",
    borderRadius: "10px",
    border: "1px solid rgba(0, 97, 254, 0.18)",
    bgcolor: "background.card",
    minWidth: 0,
  },
  "& .mic-media-empty": {
    borderStyle: "dashed",
    borderColor: "rgba(148,163,184,0.45)",
    background: "transparent",
    color: "#94a3b8",
  },
  "& .mic-media-thumb": {
    flexShrink: "0 !important",
    width: "44px !important",
    minWidth: "44px !important",
    height: "44px !important",
    borderRadius: "8px",
    overflow: "hidden",
    background: "rgba(15, 23, 42, 0.06)",
    display: "grid",
    placeItems: "center",
  },
  "& .mic-media-thumb img": {
    width: "44px !important",
    minWidth: "44px !important",
    height: "44px !important",
    objectFit: "cover",
    display: "block",
  },
  "& .mic-media-thumb-fallback": {
    fontSize: "11px !important",
    fontWeight: 800,
    letterSpacing: "0.04em",
    color: AI_ASSISTANT_BLUE,
    background: "rgba(0, 97, 254, 0.1)",
  },
  "& .mic-media-meta": {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
    flex: 1,
  },
  "& .mic-media-badge": {
    display: "inline-block",
    width: "fit-content",
    maxWidth: "100%",
    padding: "1px 7px",
    borderRadius: "999px",
    fontSize: "10px !important",
    fontWeight: 800,
    letterSpacing: "0.04em",
    lineHeight: 1.5,
  },
  "& .mic-media-badge-3d": {
    color: "#1e3a8a",
    background: "rgba(0, 97, 254, 0.12)",
  },
  "& .mic-media-badge-slide": {
    color: "#065f46",
    background: "rgba(16, 185, 129, 0.14)",
  },
  "& .mic-media-title": {
    fontSize: "13px !important",
    fontWeight: 700,
    color: "ink.strong",
    lineHeight: 1.35,
  },
  "& .mic-media-note": {
    fontSize: "11px !important",
    fontWeight: 600,
    color: "#64748b",
    lineHeight: 1.3,
  },
};

const chipButtonSx = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: 13,
  borderRadius: 999,
  px: 1.4,
  color: "ink.body",
  borderColor: "rgba(148,163,184,0.55)",
  bgcolor: "background.card",
  "&:hover": {
    borderColor: AI_ASSISTANT_BLUE,
    bgcolor: "rgba(0, 97, 254, 0.06)",
    color: AI_ASSISTANT_BLUE,
  },
};

export default function AIChatMessage({
  message,
  onRetry,
  suggestions,
  onSuggestionSelect,
  suggestionsDisabled,
  draftSlides,
  onDeckAction,
}) {
  if (message.event) {
    // Draft-card result (created / discarded / saved). Not a counted message.
    return (
      <Box role="status" sx={{ display: "flex", justifyContent: "center", mb: 1.25, px: 1 }}>
        <Box
          sx={{
            maxWidth: "92%",
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            bgcolor: "rgba(16,185,129,0.08)",
            border: "1px solid rgba(16,185,129,0.25)",
            color: "#065f46",
            fontSize: 12.5,
            ...bubbleContentSx,
            "& *": { color: "#065f46", fontSize: "12.5px !important" },
          }}
        >
          {looksLikeHtml(message.content)
            ? parse(message.content, { replace: stripAntiWrapStyles })
            : looksLikeMarkdown(message.content)
              ? renderMarkdown(message.content)
              : message.content}
          {Array.isArray(suggestions) && suggestions.length ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, mt: 0.9, justifyContent: "center" }}>
              {suggestions.map((action) => (
                <Button
                  key={action.id || action.label}
                  size="small"
                  variant="outlined"
                  disabled={suggestionsDisabled}
                  onClick={() => onSuggestionSelect?.(action)}
                  sx={chipButtonSx}
                >
                  {action.label}
                </Button>
              ))}
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  }
  return <ChatBubble {...{ message, onRetry, suggestions, onSuggestionSelect, suggestionsDisabled, draftSlides, onDeckAction }} />;
}

function ChatBubble({
  message,
  onRetry,
  suggestions,
  onSuggestionSelect,
  suggestionsDisabled,
  draftSlides,
  onDeckAction,
}) {
  const isUser = message.role === "user";
  const failed = message.status === MESSAGE_STATUS.FAILED;
  const content = message.content || "";
  const html = !isUser && looksLikeHtml(content);
  const suggestionList = !isUser && Array.isArray(suggestions) ? suggestions : [];
  const [editingIndex, setEditingIndex] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editBullets, setEditBullets] = useState("");

  const startEdit = (index) => {
    const slide = Array.isArray(draftSlides) ? draftSlides[index] : null;
    if (!slide || !onDeckAction) return;
    setEditingIndex(index);
    setEditTitle(slide.title || "");
    setEditBullets((Array.isArray(slide.bullets) ? slide.bullets : []).join("\n"));
  };

  const saveEdit = () => {
    if (editingIndex == null) return;
    onDeckAction?.("save-slide", {
      index: editingIndex,
      title: editTitle.trim(),
      bullets: editBullets
        .split("\n")
        .map((line) => line.replace(/^[-•*]\s*/, "").trim())
        .filter(Boolean)
        .slice(0, 8),
    });
    setEditingIndex(null);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: isUser ? "flex-end" : "flex-start",
        mb: 1.5,
        px: 0.5,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          // Definite width so wrapping has a real boundary; padding stays inside.
          width: isUser ? "fit-content" : "92%",
          maxWidth: "92%",
          minWidth: 0,
          boxSizing: "border-box",
          px: 2,
          py: 1.5,
          borderRadius: isUser ? "18px 18px 6px 18px" : "18px 18px 18px 6px",
          bgcolor: isUser ? AI_ASSISTANT_BLUE : "#ffffff",
          color: isUser ? "#fff" : "#0f172a",
          border: isUser ? "none" : "1px solid rgba(148,163,184,0.28)",
          boxShadow: isUser
            ? "0 8px 18px rgba(0, 97, 254, 0.18)"
            : "0 4px 14px rgba(15, 23, 42, 0.04)",
          opacity: failed ? 0.85 : 1,
          outline: failed ? "1px solid #ef4444" : "none",
          ...bubbleContentSx,
        }}
      >
        {html ? (
          <Box sx={bubbleContentSx}>
            {parse(content, {
              replace(domNode) {
                stripAntiWrapStyles(domNode);
                if (domNode?.type !== "tag" || String(domNode.name).toLowerCase() !== "button") {
                  return undefined;
                }
                const action = domNode.attribs?.["data-mic-action"];
                if (!action) return undefined;
                const index = Number(domNode.attribs?.["data-mic-index"]);
                return (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={suggestionsDisabled || !onDeckAction}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (action === "edit-slide") {
                        startEdit(index);
                        return;
                      }
                      if (action === "delete-slide") {
                        onDeckAction?.("delete-slide", index);
                        if (editingIndex === index) setEditingIndex(null);
                      }
                    }}
                    sx={{
                      ...chipButtonSx,
                      minWidth: 0,
                      px: 1.1,
                      py: 0.2,
                      fontSize: 12,
                      color: action === "delete-slide" ? "#b91c1c" : "#334155",
                    }}
                  >
                    {domToReact(domNode.children || [])}
                  </Button>
                );
              },
            })}
            {editingIndex != null ? (
              <Stack spacing={1} sx={{ mt: 1.25 }}>
                <TextField
                  size="small"
                  label="Slide title"
                  value={editTitle}
                  disabled={suggestionsDisabled}
                  onChange={(event) => setEditTitle(event.target.value)}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Bullets (one per line)"
                  value={editBullets}
                  disabled={suggestionsDisabled}
                  onChange={(event) => setEditBullets(event.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={8}
                />
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    disabled={suggestionsDisabled}
                    onClick={saveEdit}
                    sx={{ textTransform: "none", fontWeight: 700, bgcolor: AI_ASSISTANT_BLUE }}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={suggestionsDisabled}
                    onClick={() => {
                      onDeckAction?.("delete-slide", editingIndex);
                      setEditingIndex(null);
                    }}
                    sx={{ ...chipButtonSx, color: "#b91c1c" }}
                  >
                    Delete
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={suggestionsDisabled}
                    onClick={() => onDeckAction?.("discard")}
                    sx={chipButtonSx}
                  >
                    Discard
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={suggestionsDisabled}
                    onClick={() => onDeckAction?.("make-presentation")}
                    sx={chipButtonSx}
                  >
                    Make presentation
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    disabled={suggestionsDisabled}
                    onClick={() => setEditingIndex(null)}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Stack>
            ) : null}
          </Box>
        ) : !isUser && looksLikeMarkdown(content) ? (
          <Box sx={bubbleContentSx}>{renderMarkdown(content)}</Box>
        ) : (
          <Typography
            component="div"
            sx={{
              fontSize: 14,
              lineHeight: 1.55,
              minWidth: 0,
              maxWidth: "100%",
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            {content}
          </Typography>
        )}
        {suggestionList.length ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.8,
              mt: 1.25,
              pt: 1.25,
              borderTop: "1px solid rgba(148,163,184,0.22)",
            }}
          >
            {suggestionList.map((action) => (
              <Button
                key={action.id || action.label}
                size="small"
                variant="outlined"
                disabled={suggestionsDisabled}
                onClick={() => onSuggestionSelect?.(action)}
                sx={chipButtonSx}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        ) : null}
      </Box>
      {failed && typeof onRetry === "function" ? (
        <Button
          size="small"
          startIcon={<RefreshRoundedIcon fontSize="small" />}
          onClick={() => onRetry(message)}
          sx={{ mt: 0.6, textTransform: "none", fontSize: 12, color: "#b91c1c" }}
        >
          Retry
        </Button>
      ) : null}
    </Box>
  );
}

ChatBubble.propTypes = {
  message: PropTypes.object.isRequired,
  onRetry: PropTypes.func,
  suggestions: PropTypes.arrayOf(PropTypes.object),
  onSuggestionSelect: PropTypes.func,
  suggestionsDisabled: PropTypes.bool,
  draftSlides: PropTypes.arrayOf(PropTypes.object),
  onDeckAction: PropTypes.func,
};

AIChatMessage.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string,
    role: PropTypes.string,
    content: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  onRetry: PropTypes.func,
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      prompt: PropTypes.string,
    })
  ),
  onSuggestionSelect: PropTypes.func,
  suggestionsDisabled: PropTypes.bool,
  draftSlides: PropTypes.arrayOf(PropTypes.object),
  onDeckAction: PropTypes.func,
};
